// src/admin/ShippingTaxSettings.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Save, Truck, DollarSign, Percent, AlertCircle, CheckCircle,
  Plus, Trash2, Home, Zap
} from 'lucide-react';
import SuccessModal from '../components/SuccessModal';

const ShippingTaxSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSuccess, setShowSuccess] = useState(false);

  const [settings, setSettings] = useState({
    // Shipping
    shippingEnabled: true,
    defaultShippingFee: 200,
    freeShippingEnabled: true,
    freeShippingLimit: 2000,
    shippingMethods: [
      { id: 'standard', name: 'Standard Delivery', price: 200, days: '3-5 days', active: true },
    ],

    // Tax
    taxEnabled: false,
    taxName: 'GST',
    taxRate: 0, // percentage
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const docRef = doc(db, 'settings', 'shipping-tax');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setSettings(prev => ({ ...prev, ...data }));
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await setDoc(doc(db, 'settings', 'shipping-tax'), settings, { merge: true });
      setMessage({ type: 'success', text: 'Settings saved!' });
      setShowSuccess(true);
      window.dispatchEvent(new Event('shippingTaxUpdated'));
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Save error:', error);
      setMessage({ type: 'error', text: 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  // Add shipping method
  const addShippingMethod = () => {
    const newMethod = {
      id: Date.now().toString(),
      name: '',
      price: 0,
      days: '',
      active: true,
    };
    setSettings({
      ...settings,
      shippingMethods: [...settings.shippingMethods, newMethod],
    });
  };

  const updateShippingMethod = (id, field, value) => {
    setSettings({
      ...settings,
      shippingMethods: settings.shippingMethods.map(m =>
        m.id === id ? { ...m, [field]: value } : m
      ),
    });
  };

  const removeShippingMethod = (id) => {
    if (settings.shippingMethods.length === 1) {
      alert('Kam az kam ek shipping method zaroori hai.');
      return;
    }
    setSettings({
      ...settings,
      shippingMethods: settings.shippingMethods.filter(m => m.id !== id),
    });
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-green-700 border-t-transparent"></div>
        <p className="mt-4 text-gray-500">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">

      <div>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Truck className="text-green-700" size={26} /> Shipping & Tax Settings
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Shipping fees aur tax rate yahan se control karein.
        </p>
      </div>

      {message.text && (
        <div className={`text-sm p-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">

        {/* ============ SHIPPING SECTION ============ */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Truck size={20} className="text-green-700" /> Shipping Settings
            </h2>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={settings.shippingEnabled}
                onChange={(e) => setSettings({ ...settings, shippingEnabled: e.target.checked })}
                className="w-5 h-5 text-green-700" />
              <span className="text-sm font-semibold text-gray-700">Enabled</span>
            </label>
          </div>

          {settings.shippingEnabled && (
            <>
              {/* Free Shipping */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-5">
                <label className="flex items-center gap-3 cursor-pointer mb-3">
                  <input type="checkbox" checked={settings.freeShippingEnabled}
                    onChange={(e) => setSettings({ ...settings, freeShippingEnabled: e.target.checked })}
                    className="w-5 h-5 text-green-700" />
                  <span className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <Zap size={14} className="text-yellow-600" /> Free Shipping Enable
                  </span>
                </label>

                {settings.freeShippingEnabled && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Free Shipping on Orders Above (Rs.)
                    </label>
                    <input type="number" value={settings.freeShippingLimit}
                      onChange={(e) => setSettings({ ...settings, freeShippingLimit: Number(e.target.value) })}
                      className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                      placeholder="e.g., 2000" min="0" />
                    <p className="text-xs text-green-700 mt-1">
                      ✓ Rs. {settings.freeShippingLimit} se upar ke orders par free shipping milegi.
                    </p>
                  </div>
                )}
              </div>

              {/* Default Shipping Fee */}
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Default Shipping Fee (Rs.)
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-3.5 text-gray-400" size={18} />
                  <input type="number" value={settings.defaultShippingFee}
                    onChange={(e) => setSettings({ ...settings, defaultShippingFee: Number(e.target.value) })}
                    className="w-full border border-gray-300 rounded-lg py-3 pl-11 pr-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="e.g., 200" min="0" />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Ye fee free shipping limit se kam orders par lagegi.
                </p>
              </div>

              {/* Shipping Methods */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-gray-700">
                    Shipping Methods (Optional)
                  </label>
                  <button type="button" onClick={addShippingMethod}
                    className="text-xs font-semibold text-green-700 hover:text-green-800 flex items-center gap-1">
                    <Plus size={12} /> Add Method
                  </button>
                </div>

                <div className="space-y-2">
                  {settings.shippingMethods.map(method => (
                    <div key={method.id} className="grid grid-cols-12 gap-2 p-3 bg-gray-50 rounded-lg">
                      <input type="text" value={method.name}
                        onChange={(e) => updateShippingMethod(method.id, 'name', e.target.value)}
                        className="col-span-4 border border-gray-300 rounded-lg py-2 px-3 text-xs focus:outline-none focus:border-green-600"
                        placeholder="Method name" />
                      <input type="number" value={method.price}
                        onChange={(e) => updateShippingMethod(method.id, 'price', Number(e.target.value))}
                        className="col-span-3 border border-gray-300 rounded-lg py-2 px-3 text-xs focus:outline-none focus:border-green-600"
                        placeholder="Price" />
                      <input type="text" value={method.days}
                        onChange={(e) => updateShippingMethod(method.id, 'days', e.target.value)}
                        className="col-span-3 border border-gray-300 rounded-lg py-2 px-3 text-xs focus:outline-none focus:border-green-600"
                        placeholder="e.g., 3-5 days" />
                      <button type="button" onClick={() => removeShippingMethod(method.id)}
                        className="col-span-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg flex items-center justify-center">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Ye methods checkout par select karne ke liye dikhengi. (Advanced feature)
                </p>
              </div>
            </>
          )}
        </div>

        {/* ============ TAX SECTION ============ */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Percent size={20} className="text-green-700" /> Tax Settings
            </h2>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={settings.taxEnabled}
                onChange={(e) => setSettings({ ...settings, taxEnabled: e.target.checked })}
                className="w-5 h-5 text-green-700" />
              <span className="text-sm font-semibold text-gray-700">Enabled</span>
            </label>
          </div>

          {settings.taxEnabled ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tax Name
                </label>
                <input type="text" value={settings.taxName}
                  onChange={(e) => setSettings({ ...settings, taxName: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                  placeholder="e.g., GST, VAT" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tax Rate (%)
                </label>
                <div className="relative">
                  <Percent className="absolute left-3 top-3.5 text-gray-400" size={18} />
                  <input type="number" step="0.01" value={settings.taxRate}
                    onChange={(e) => setSettings({ ...settings, taxRate: Number(e.target.value) })}
                    className="w-full border border-gray-300 rounded-lg py-3 pl-11 pr-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="e.g., 17 (for 17%)" min="0" max="100" />
                </div>
              </div>

              <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800 flex items-start gap-2">
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>
                    Tax <strong>subtotal + shipping</strong> par calculate hoga. Ye checkout par "Tax ({settings.taxName} {settings.taxRate}%)" ki tarah dikhega.
                  </span>
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              Tax disabled hai. Enable karein aur rate set karein.
            </p>
          )}
        </div>

        {/* Save */}
        <div className="flex justify-end sticky bottom-4">
          <button type="submit" disabled={saving}
            className={`bg-green-700 text-white py-3 px-8 rounded-lg font-semibold hover:bg-green-800 transition flex items-center gap-2 shadow-xl ${
              saving ? 'opacity-70 cursor-not-allowed' : ''
            }`}>
            <Save size={18} /> {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

      </form>

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Success!"
        message="Shipping & Tax settings saved successfully!"
        buttonText="Great!"
      />

    </div>
  );
};

export default ShippingTaxSettings;